/**
 * Math explainer + interactive mini-demos for the Election Lab.
 *
 * Framework-agnostic: `mountMathExplainer(root)` builds the whole explainer
 * (prose, formulas, and a small live canvas demo per section) inside `root`
 * and returns a cleanup function. Used by both the React /lab page and the
 * standalone election-sim.html so the two stay identical.
 */

const STYLE_ID = 'mx-styles';
const MX_STYLES = `
  .mx-root { max-width: 960px; }
  .mx-root h3 { font-size: 16px; margin: 26px 0 6px; color: #e7ecf5; }
  .mx-root p { color: #aab6d4; font-size: 13.5px; margin: 0 0 10px; }
  .mx-root p b { color: #e7ecf5; font-weight: 600; }
  .mx-intro { color: #93a0bd; font-size: 13.5px; margin-bottom: 4px; }
  .mx-formula {
    display: block; background: #0d1424; border: 1px solid #26304a; border-left: 3px solid #6ea8fe;
    border-radius: 8px; padding: 10px 14px; margin: 10px 0; color: #cfe0ff;
    font: 15px/1.5 'Cambria Math', 'Times New Roman', Georgia, serif; overflow-x: auto;
  }
  .mx-formula .v { color: #7ef0c2; font-style: italic; }
  .mx-demo { background: #121826; border: 1px solid #26304a; border-radius: 0 10px 10px 10px; padding: 12px; margin: 0 0 4px; }
  .mx-demo canvas { display: block; width: 100%; border-radius: 8px; background: #0a0e18; }
  .mx-tabs { display: flex; gap: 6px; margin: 8px 0 0; }
  .mx-tab { background: #0d1424; color: #93a0bd; border: 1px solid #26304a; border-bottom: none;
    padding: 6px 12px; border-radius: 8px 8px 0 0; cursor: pointer; font-size: 12px; font-weight: 600; }
  .mx-tab.on { background: #121826; color: #e7ecf5; }
  .mx-real { background: #121826; border: 1px solid #26304a; border-radius: 0 10px 10px 10px; padding: 12px; margin: 0 0 4px; }
  .mx-real canvas { display: block; width: 100%; border-radius: 8px; background: #0a0e18; }
  .mx-real .cap { font-size: 12.5px; color: #aab6d4; margin-top: 10px; }
  .mx-real .cap b { color: #e7ecf5; }
  .mx-real .src { font-size: 11px; color: #6b7897; margin-top: 6px; }
  .mx-legend { display: flex; flex-wrap: wrap; gap: 6px 14px; margin-top: 8px; font-size: 11px; color: #93a0bd; }
  .mx-legend i { width: 10px; height: 10px; border-radius: 2px; display: inline-block; margin-right: 4px; vertical-align: middle; }
  .mx-row { display: flex; flex-wrap: wrap; gap: 8px 12px; align-items: center; margin-bottom: 10px; }
  .mx-row .lbl { font-size: 11px; color: #93a0bd; }
  .mx-btn {
    background: #171f30; color: #e7ecf5; border: 1px solid #26304a; padding: 6px 11px;
    border-radius: 8px; cursor: pointer; font-size: 12px; font-weight: 600;
  }
  .mx-btn:hover { border-color: #6ea8fe; }
  .mx-btn.on { background: linear-gradient(180deg, #3f6fe0, #2f56c0); border-color: #4b7bf0; }
  .mx-readout { font-size: 13px; color: #cfe0ff; margin-top: 8px; }
  .mx-readout b { color: #7ef0c2; }
  .mx-root input[type=range] { accent-color: #6ea8fe; vertical-align: middle; }
  .mx-slider { display: flex; align-items: center; gap: 6px; font-size: 11px; color: #93a0bd; }
  .mx-note { font-size: 12px; color: #7c88a6; font-style: italic; margin-top: 6px; }
`;

export function mountMathExplainer(root) {
  if (!document.getElementById(STYLE_ID)) {
    const st = document.createElement('style');
    st.id = STYLE_ID;
    st.textContent = MX_STYLES;
    document.head.appendChild(st);
  }

  root.classList.add('mx-root');

  // Each section gets an Interactive/Real-world tab pair over a shared body.
  const panel = (key) => `
    <div class="mx-tabs">
      <button class="mx-tab on" data-for="${key}" data-kind="demo">🧪 Interactive</button>
      <button class="mx-tab" data-for="${key}" data-kind="real">🌍 Real-world example</button>
    </div>
    <div class="mx-demo" data-demo="${key}"></div>
    <div class="mx-real" data-real="${key}" style="display:none"></div>`;

  root.innerHTML = `
    <p class="mx-intro">Every number the simulator shows comes from the equations below. Each section has a live
    mini-demo — drag, toggle, and slide to see exactly what the math does.</p>

    <h3>1 · Where the voters come from (sampling)</h3>
    <p>Voters are fixed points in a 2-D opinion square. Clustered landscapes draw each coordinate from a bell curve
    using the <b>Box–Muller transform</b>:</p>
    <span class="mx-formula">x = μ + σ·√(−2·ln <span class="v">u</span>)·cos(2π <span class="v">v</span>),
      &nbsp; with <span class="v">u</span>, <span class="v">v</span> ~ Uniform(0,1)</span>
    ${panel('sampling')}

    <h3>2 · How a voter picks a party (spatial utility)</h3>
    <p>A voter prefers the <b>nearest</b> party. Utility = −distance, ranked by <b>squared</b> Euclidean distance
    (same ordering as distance, but no square root to compute):</p>
    <span class="mx-formula">d²(<span class="v">v</span>, <span class="v">p</span>) =
      (p<sub>x</sub> − v<sub>x</sub>)² + (p<sub>y</sub> − v<sub>y</sub>)²</span>
    <p>Coloring every point by its nearest party carves the space into <b>Voronoi regions</b> — each party's territory.
    Move your cursor to see the nearest-party line.</p>
    ${panel('voronoi')}

    <h3>3 · Strategic voting (the “psychological effect”)</h3>
    <p>A party is <b>viable</b> if it's in the top 2 by share or above the survival floor τ. Under FPTP, a voter whose
    favorite isn't viable defects — with probability set by the slider — to their nearest viable party:</p>
    <span class="mx-formula">vote = argmax<sub>p ∈ Viable</sub> (−d²)  &nbsp;if rand &lt; strat and favorite ∉ Viable</span>
    <p>Watch small parties bleed support to the big two as you raise the strategic share.</p>
    ${panel('strategic')}

    <h3>4 · Turning votes into seats (the four rules)</h3>
    <p>Same election, four counting methods — often three different winners. Switch tabs to see each rule's math.</p>
    ${panel('tally')}

    <h3>5 · Effective Number of Parties (ENP)</h3>
    <p>The headline metric is the <b>Laakso–Taagepera index</b> — the inverse of the Herfindahl concentration of vote
    shares p<sub>i</sub>:</p>
    <span class="mx-formula">ENP = 1 / Σ p<sub>i</sub>²</span>
    <p>Two equal parties → 2.0. Drag the shares and watch ENP track how many parties <i>effectively</i> matter.</p>
    ${panel('enp')}

    <h3>6 · How parties evolve (this is k-means)</h3>
    <p>After each election every party slides toward the <b>mean position of its own voters</b>. Voters-pick-nearest
    then parties-move-to-their-mean is exactly one step of <b>Lloyd's algorithm</b> (k-means clustering):</p>
    <span class="mx-formula"><span class="v">p</span> ← <span class="v">p</span> + η·(c̄<sub>p</sub> − <span class="v">p</span>),
      &nbsp; c̄<sub>p</sub> = mean of p's supporters</span>
    <p>Press <b>Step</b> to run iterations and watch the centroids settle onto the vote clusters.</p>
    ${panel('kmeans')}

    <h3>7 · Why it collapses to two (the feedback loop)</h3>
    <p>Sections 3, 5 and death combine into a self-reinforcing spiral under single-winner rules:</p>
    <span class="mx-formula">shares → viability → strategic defection → smaller shares → death → repeat</span>
    <p>Run the loop and watch ENP contract toward 2 — Duverger's Law emerging from the arithmetic.</p>
    ${panel('feedback')}
  `;

  const cleanups = [];
  const raf = (fn) => {
    const id = requestAnimationFrame(fn);
    cleanups.push(() => cancelAnimationFrame(id));
    return id;
  };

  // shared canvas helper --------------------------------------------------
  function makeCanvas(host, height) {
    const cv = document.createElement('canvas');
    cv.style.height = `${height}px`;
    host.appendChild(cv);
    const ctx = cv.getContext('2d');
    const fit = () => {
      const dpr = window.devicePixelRatio || 1;
      const r = cv.getBoundingClientRect();
      cv.width = r.width * dpr;
      cv.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w: r.width, h: r.height };
    };
    return { cv, ctx, fit };
  }
  const gaussian = (m, s) => {
    let u = 0;
    let v = 0;
    while (u === 0) u = Math.random();
    while (v === 0) v = Math.random();
    return m + s * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  };
  const clamp01 = (x) => Math.max(0.02, Math.min(0.98, x));
  const COLORS = ['#6ea8fe', '#ff8f6e', '#7ef0c2', '#c8a8ff', '#ffd257', '#f472b6'];
  const btn = (label, on) => `<button class="mx-btn${on ? ' on' : ''}">${label}</button>`;
  const host = (name) => root.querySelector(`[data-demo="${name}"]`);

  // ---- Demo 1: sampling -------------------------------------------------
  (function sampling() {
    const h = host('sampling');
    h.innerHTML = `<div class="mx-row">
      ${btn('Uniform', false)}${btn('One center', true)}${btn('Two clusters', false)}${btn('Three clusters', false)}
      <span class="lbl">— 800 sampled voters</span></div>`;
    const { ctx, fit } = makeCanvas(h, 200);
    const buttons = [...h.querySelectorAll('.mx-btn')];
    const modes = ['uniform', 'one', 'two', 'three'];
    const centers = {
      one: [[0.5, 0.5]],
      two: [
        [0.32, 0.45],
        [0.7, 0.6],
      ],
      three: [
        [0.3, 0.35],
        [0.72, 0.4],
        [0.5, 0.75],
      ],
    };
    let pts = [];
    const gen = (mode) => {
      pts = [];
      const cs = centers[mode];
      for (let i = 0; i < 800; i++) {
        if (!cs) pts.push([Math.random(), Math.random()]);
        else {
          const c = cs[(Math.random() * cs.length) | 0];
          pts.push([clamp01(gaussian(c[0], 0.12)), clamp01(gaussian(c[1], 0.12))]);
        }
      }
      render();
    };
    function render() {
      const { w, h: hh } = fit();
      ctx.clearRect(0, 0, w, hh);
      ctx.fillStyle = '#6ea8fe';
      ctx.globalAlpha = 0.6;
      for (const [x, y] of pts) {
        ctx.beginPath();
        ctx.arc(8 + x * (w - 16), 8 + y * (hh - 16), 1.8, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }
    buttons.forEach((b, i) =>
      b.addEventListener('click', () => {
        buttons.forEach((x) => x.classList.remove('on'));
        b.classList.add('on');
        gen(modes[i]);
      })
    );
    gen('one');
    window.addEventListener('resize', render);
    cleanups.push(() => window.removeEventListener('resize', render));
  })();

  // ---- Demo 2: Voronoi / nearest party ----------------------------------
  (function voronoi() {
    const h = host('voronoi');
    h.innerHTML = `<div class="mx-row"><span class="lbl">Move cursor over the field — line points to the nearest party</span></div>`;
    const { cv, ctx, fit } = makeCanvas(h, 220);
    const parties = [
      [0.25, 0.3],
      [0.72, 0.28],
      [0.5, 0.72],
      [0.2, 0.75],
    ];
    let mouse = null;
    function render() {
      const { w, h: hh } = fit();
      const X = (x) => 8 + x * (w - 16);
      const Y = (y) => 8 + y * (hh - 16);
      const step = 7;
      for (let sx = 0; sx < w; sx += step) {
        for (let sy = 0; sy < hh; sy += step) {
          const nx = (sx - 8) / (w - 16);
          const ny = (sy - 8) / (hh - 16);
          let best = 0;
          let bd = Infinity;
          parties.forEach((p, i) => {
            const d = (p[0] - nx) ** 2 + (p[1] - ny) ** 2;
            if (d < bd) {
              bd = d;
              best = i;
            }
          });
          ctx.fillStyle = COLORS[best];
          ctx.globalAlpha = 0.16;
          ctx.fillRect(sx, sy, step, step);
        }
      }
      ctx.globalAlpha = 1;
      parties.forEach((p, i) => {
        ctx.fillStyle = COLORS[i];
        ctx.beginPath();
        ctx.arc(X(p[0]), Y(p[1]), 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      });
      if (mouse) {
        let best = 0;
        let bd = Infinity;
        parties.forEach((p, i) => {
          const d = (p[0] - mouse[0]) ** 2 + (p[1] - mouse[1]) ** 2;
          if (d < bd) {
            bd = d;
            best = i;
          }
        });
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.moveTo(X(mouse[0]), Y(mouse[1]));
        ctx.lineTo(X(parties[best][0]), Y(parties[best][1]));
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(X(mouse[0]), Y(mouse[1]), 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.font = '700 11px sans-serif';
        ctx.fillText(`d² = ${bd.toFixed(3)}`, X(mouse[0]) + 8, Y(mouse[1]) - 6);
      }
    }
    const onMove = (e) => {
      const r = cv.getBoundingClientRect();
      mouse = [
        (e.clientX - r.left - 8) / (r.width - 16),
        (e.clientY - r.top - 8) / (r.height - 16),
      ];
      render();
    };
    cv.addEventListener('mousemove', onMove);
    cv.addEventListener('mouseleave', () => {
      mouse = null;
      render();
    });
    render();
    window.addEventListener('resize', render);
    cleanups.push(() => window.removeEventListener('resize', render));
  })();

  // ---- Demo 3: strategic voting -----------------------------------------
  (function strategic() {
    const h = host('strategic');
    h.innerHTML = `<div class="mx-row">
      <span class="mx-slider">Strategic voting <input type="range" min="0" max="100" value="0" style="width:160px"> <b class="sv">0%</b></span>
      </div><div class="mx-readout"></div>`;
    const { ctx, fit } = makeCanvas(h, 200);
    const slider = h.querySelector('input');
    const svLbl = h.querySelector('.sv');
    const readout = h.querySelector('.mx-readout');
    const base = [0.3, 0.26, 0.18, 0.14, 0.12]; // A..E first-pref shares
    const names = ['A', 'B', 'C', 'D', 'E'];
    const enp = (arr) => 1 / arr.reduce((s, p) => s + p * p, 0);
    function shares(strat) {
      // top-2 are viable; non-viable lose `strat` of their votes to the viable
      // two in proportion to viable size (a simplification of nearest-viable).
      const out = base.slice();
      const viableTotal = base[0] + base[1];
      let moved = 0;
      for (let i = 2; i < out.length; i++) {
        const take = out[i] * strat;
        out[i] -= take;
        moved += take;
      }
      out[0] += moved * (base[0] / viableTotal);
      out[1] += moved * (base[1] / viableTotal);
      return out;
    }
    function render() {
      const strat = +slider.value / 100;
      const s = shares(strat);
      const { w, h: hh } = fit();
      ctx.clearRect(0, 0, w, hh);
      const n = s.length;
      const bw = (w - 40) / n;
      const maxH = hh - 40;
      s.forEach((v, i) => {
        const bh = v * maxH * 2;
        const x = 20 + i * bw;
        ctx.fillStyle = i < 2 ? COLORS[i] : '#4a5578';
        ctx.fillRect(x + 6, hh - 24 - bh, bw - 12, bh);
        ctx.fillStyle = '#cfe0ff';
        ctx.font = '700 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`${(v * 100).toFixed(0)}%`, x + bw / 2, hh - 28 - bh);
        ctx.fillStyle = i < 2 ? '#e7ecf5' : '#7c88a6';
        ctx.fillText(names[i] + (i < 2 ? ' ✓' : ''), x + bw / 2, hh - 8);
      });
      svLbl.textContent = `${slider.value}%`;
      readout.innerHTML = `Viable parties: <b>A, B</b> &nbsp;·&nbsp; ENP: <b>${enp(base).toFixed(
        2
      )} → ${enp(s).toFixed(2)}</b>`;
    }
    slider.addEventListener('input', render);
    render();
    window.addEventListener('resize', render);
    cleanups.push(() => window.removeEventListener('resize', render));
  })();

  // ---- Demo 4: tallying (FPTP / IRV / D'Hondt / Approval) ----------------
  (function tally() {
    const h = host('tally');
    h.innerHTML = `<div class="mx-row" data-tabs>
      ${btn('FPTP', true)}${btn('Ranked (IRV)', false)}${btn('D’Hondt (PR)', false)}${btn('Approval', false)}
      </div><div class="mx-readout" data-out></div>`;
    const { ctx, fit } = makeCanvas(h, 220);
    const out = h.querySelector('[data-out]');
    const tabs = [...h.querySelector('[data-tabs]').querySelectorAll('.mx-btn')];
    const cand = ['A', 'B', 'C'];
    const first = { A: 35, B: 33, C: 32 };
    // full rankings for IRV (C's second prefs decide the comeback)
    const irvBallots = [
      { order: ['A', 'C', 'B'], n: 35 },
      { order: ['B', 'C', 'A'], n: 33 },
      { order: ['C', 'B', 'A'], n: 20 },
      { order: ['C', 'A', 'B'], n: 12 },
    ];
    const approvals = { A: 40, B: 42, C: 58 };
    const color = { A: COLORS[0], B: COLORS[1], C: COLORS[2] };
    let mode = 'fptp';
    let mag = 3;

    function bars(values, winners, labelFn) {
      const { w, h: hh } = fit();
      ctx.clearRect(0, 0, w, hh);
      const keys = Object.keys(values);
      const bw = (w - 40) / keys.length;
      const max = Math.max(...Object.values(values), 1);
      keys.forEach((k, i) => {
        const bh = (values[k] / max) * (hh - 56);
        const x = 20 + i * bw;
        const win = winners.includes(k);
        ctx.fillStyle = color[k];
        ctx.globalAlpha = win ? 1 : 0.5;
        ctx.fillRect(x + 8, hh - 30 - bh, bw - 16, bh);
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#cfe0ff';
        ctx.font = '700 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(labelFn ? labelFn(k) : values[k], x + bw / 2, hh - 34 - bh);
        ctx.fillStyle = win ? '#7ef0c2' : '#93a0bd';
        ctx.fillText(k + (win ? ' ★' : ''), x + bw / 2, hh - 10);
      });
    }

    function renderFptp() {
      const win = cand.reduce((a, b) => (first[a] >= first[b] ? a : b));
      bars(first, [win]);
      out.innerHTML = `Plurality: winner = argmax first-pref = <b>${win}</b> (${first[win]}%). Runners-up win nothing.`;
    }
    function renderIrv() {
      const elim = new Set();
      const rounds = [];
      for (;;) {
        const t = { A: 0, B: 0, C: 0 };
        irvBallots.forEach((b) => {
          const top = b.order.find((c) => !elim.has(c));
          if (top) t[top] += b.n;
        });
        Object.keys(t).forEach((k) => elim.has(k) && delete t[k]);
        rounds.push({ ...t });
        const total = Object.values(t).reduce((a, c) => a + c, 0);
        const lead = Object.keys(t).reduce((a, b) => (t[a] >= t[b] ? a : b));
        if (t[lead] > total / 2 || Object.keys(t).length <= 2) break;
        const lo = Object.keys(t).reduce((a, b) => (t[a] <= t[b] ? a : b));
        elim.add(lo);
      }
      const final = rounds[rounds.length - 1];
      const win = Object.keys(final).reduce((a, b) => (final[a] >= final[b] ? a : b));
      bars(final, [win]);
      const txt = rounds
        .map(
          (r, i) =>
            `R${i + 1}: ${Object.entries(r)
              .map(([k, v]) => `${k}=${v}`)
              .join(', ')}`
        )
        .join(' → ');
      out.innerHTML = `Eliminate lowest, transfer to next choice until majority. <b>${txt}</b> → winner <b>${win}</b> (a comeback: A led round 1).`;
    }
    function renderDhondt() {
      const seats = { A: 0, B: 0, C: 0 };
      const picks = [];
      for (let s = 0; s < mag; s++) {
        let best = null;
        let bq = -1;
        cand.forEach((k) => {
          const q = first[k] / (seats[k] + 1);
          if (q > bq) {
            bq = q;
            best = k;
          }
        });
        seats[best]++;
        picks.push(`${best} (${bq.toFixed(1)})`);
      }
      bars(
        seats,
        cand.filter((k) => seats[k] > 0),
        (k) => `${seats[k]} seat${seats[k] === 1 ? '' : 's'}`
      );
      out.innerHTML = `M = ${mag}. Each seat → highest quotient v/(seats+1). Order: <b>${picks.join(
        ' · '
      )}</b>. <span class="mx-slider" style="margin-left:8px">M <input type="range" min="1" max="5" value="${mag}" style="width:110px"></span>`;
      const ms = out.querySelector('input');
      if (ms)
        ms.addEventListener('input', (e) => {
          mag = +e.target.value;
          renderDhondt();
        });
    }
    function renderApproval() {
      const win = cand.reduce((a, b) => (approvals[a] >= approvals[b] ? a : b));
      bars(approvals, [win], (k) => `${approvals[k]}`);
      out.innerHTML = `Voters approve everyone acceptable; most approvals wins → <b>${win}</b> (the broadly-liked centrist that FPTP ignored).`;
    }
    const renderers = {
      fptp: renderFptp,
      irv: renderIrv,
      dhondt: renderDhondt,
      approval: renderApproval,
    };
    const modes = ['fptp', 'irv', 'dhondt', 'approval'];
    function draw() {
      renderers[mode]();
    }
    tabs.forEach((b, i) =>
      b.addEventListener('click', () => {
        tabs.forEach((x) => x.classList.remove('on'));
        b.classList.add('on');
        mode = modes[i];
        draw();
      })
    );
    draw();
    window.addEventListener('resize', draw);
    cleanups.push(() => window.removeEventListener('resize', draw));
  })();

  // ---- Demo 5: ENP ------------------------------------------------------
  (function enpDemo() {
    const h = host('enp');
    const init = [40, 30, 18, 12];
    h.innerHTML = `<div class="mx-row">${init
      .map(
        (v, i) =>
          `<span class="mx-slider">P${i + 1} <input type="range" min="0" max="100" value="${v}" data-i="${i}" style="width:110px"></span>`
      )
      .join('')}</div><div class="mx-readout"></div>`;
    const { ctx, fit } = makeCanvas(h, 170);
    const sliders = [...h.querySelectorAll('input')];
    const readout = h.querySelector('.mx-readout');
    function render() {
      const raw = sliders.map((s) => +s.value);
      const tot = raw.reduce((a, b) => a + b, 0) || 1;
      const p = raw.map((r) => r / tot);
      const enp = 1 / p.reduce((s, x) => s + x * x, 0);
      const { w, h: hh } = fit();
      ctx.clearRect(0, 0, w, hh);
      let x = 20;
      const barW = w - 40;
      p.forEach((v, i) => {
        ctx.fillStyle = COLORS[i];
        ctx.fillRect(x, 30, v * barW, 34);
        if (v > 0.04) {
          ctx.fillStyle = '#0a0e18';
          ctx.font = '700 12px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(`${(v * 100).toFixed(0)}%`, x + (v * barW) / 2, 52);
        }
        x += v * barW;
      });
      readout.innerHTML = `Σpᵢ² = <b>${p.reduce((s, v) => s + v * v, 0).toFixed(3)}</b> &nbsp;→&nbsp; ENP = 1/Σpᵢ² = <b>${enp.toFixed(
        2
      )}</b> effective parties`;
    }
    sliders.forEach((s) => s.addEventListener('input', render));
    render();
    window.addEventListener('resize', render);
    cleanups.push(() => window.removeEventListener('resize', render));
  })();

  // ---- Demo 6: k-means evolution ----------------------------------------
  (function kmeans() {
    const h = host('kmeans');
    h.innerHTML = `<div class="mx-row">${btn('Step', false)}${btn('Reset', false)}
      <span class="lbl">3 party-centroids · voters colored by nearest</span></div>`;
    const { ctx, fit } = makeCanvas(h, 240);
    const [stepBtn, resetBtn] = h.querySelectorAll('.mx-btn');
    let voters = [];
    let cents = [];
    function seed() {
      voters = [];
      const cs = [
        [0.3, 0.35],
        [0.72, 0.4],
        [0.5, 0.75],
      ];
      for (let i = 0; i < 360; i++) {
        const c = cs[(Math.random() * cs.length) | 0];
        voters.push({ x: clamp01(gaussian(c[0], 0.1)), y: clamp01(gaussian(c[1], 0.1)), k: 0 });
      }
      cents = [
        { x: 0.5, y: 0.3, tx: 0.5, ty: 0.3 },
        { x: 0.4, y: 0.6, tx: 0.4, ty: 0.6 },
        { x: 0.6, y: 0.55, tx: 0.6, ty: 0.55 },
      ];
      assign();
      render(1);
    }
    function assign() {
      for (const v of voters) {
        let bk = 0;
        let bd = Infinity;
        cents.forEach((c, i) => {
          const d = (c.x - v.x) ** 2 + (c.y - v.y) ** 2;
          if (d < bd) {
            bd = d;
            bk = i;
          }
        });
        v.k = bk;
      }
    }
    function step() {
      assign();
      cents.forEach((c, i) => {
        const mine = voters.filter((v) => v.k === i);
        if (mine.length) {
          c.tx = mine.reduce((s, v) => s + v.x, 0) / mine.length;
          c.ty = mine.reduce((s, v) => s + v.y, 0) / mine.length;
        }
      });
      let f = 0;
      const anim = () => {
        f++;
        const t = Math.min(1, f / 18);
        cents.forEach((c) => {
          c.dx = (c.dx ?? c.x) + (c.tx - (c.dx ?? c.x)) * 0.2;
          c.dy = (c.dy ?? c.y) + (c.ty - (c.dy ?? c.y)) * 0.2;
        });
        render(t);
        if (t < 1) raf(anim);
        else {
          cents.forEach((c) => {
            c.x = c.tx;
            c.y = c.ty;
            c.dx = c.tx;
            c.dy = c.ty;
          });
          assign();
          render(1);
        }
      };
      raf(anim);
    }
    function render() {
      const { w, h: hh } = fit();
      const X = (x) => 8 + x * (w - 16);
      const Y = (y) => 8 + y * (hh - 16);
      ctx.clearRect(0, 0, w, hh);
      for (const v of voters) {
        ctx.fillStyle = COLORS[v.k];
        ctx.globalAlpha = 0.5;
        ctx.beginPath();
        ctx.arc(X(v.x), Y(v.y), 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      cents.forEach((c, i) => {
        const cx = X(c.dx ?? c.x);
        const cy = Y(c.dy ?? c.y);
        ctx.fillStyle = COLORS[i];
        ctx.beginPath();
        ctx.arc(cx, cy, 9, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2.5;
        ctx.stroke();
      });
    }
    stepBtn.addEventListener('click', step);
    resetBtn.addEventListener('click', seed);
    seed();
    window.addEventListener('resize', () => render(1));
    cleanups.push(() => window.removeEventListener('resize', render));
  })();

  // ---- Demo 7: feedback loop -> ENP contraction -------------------------
  (function feedback() {
    const h = host('feedback');
    h.innerHTML = `<div class="mx-row">${btn('Run 15 cycles', false)}${btn('Reset', false)}
      <span class="lbl">6 parties, FPTP-style strategic pressure</span></div><div class="mx-readout"></div>`;
    const { ctx, fit } = makeCanvas(h, 200);
    const [runBtn, resetBtn] = h.querySelectorAll('.mx-btn');
    const readout = h.querySelector('.mx-readout');
    const FLOOR = 0.05;
    let shares = [];
    let series = [];
    const enp = (arr) => (arr.length ? 1 / arr.reduce((s, p) => s + p * p, 0) : 0);
    function seed() {
      shares = [0.24, 0.2, 0.18, 0.16, 0.13, 0.09];
      series = [enp(shares)];
      render();
      readout.innerHTML = `ENP: <b>${enp(shares).toFixed(2)}</b>`;
    }
    function cycle() {
      const order = shares.map((s, i) => [s, i]).sort((a, b) => b[0] - a[0]);
      const viable = new Set([order[0][1], order[1][1]]);
      shares.forEach((s, i) => {
        if (s >= FLOOR) viable.add(i);
      });
      const vTot = [...viable].reduce((s, i) => s + shares[i], 0);
      let moved = 0;
      shares = shares.map((s, i) => {
        if (viable.has(i)) return s;
        moved += s * 0.5;
        return s * 0.5;
      });
      shares = shares.map((s, i) => (viable.has(i) ? s + moved * (shares[i] / vTot) : s));
      shares = shares.filter((s) => s >= 0.02);
      const t = shares.reduce((a, b) => a + b, 0);
      shares = shares.map((s) => s / t);
      series.push(enp(shares));
    }
    function render() {
      const { w, h: hh } = fit();
      ctx.clearRect(0, 0, w, hh);
      const pad = 26;
      const maxY = 6;
      const px = (i) => pad + (i / 15) * (w - pad - 8);
      const py = (v) => hh - pad - (v / maxY) * (hh - pad - 8);
      for (let g = 1; g <= 6; g++) {
        ctx.strokeStyle = g === 2 ? '#2b5c46' : '#1c2438';
        ctx.setLineDash(g === 2 ? [4, 4] : []);
        ctx.beginPath();
        ctx.moveTo(pad, py(g));
        ctx.lineTo(w - 8, py(g));
        ctx.stroke();
        ctx.fillStyle = g === 2 ? '#7ef0c2' : '#6b7897';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(g === 2 ? '2' : g, pad - 4, py(g) + 3);
      }
      ctx.setLineDash([]);
      ctx.strokeStyle = '#6ea8fe';
      ctx.lineWidth = 2;
      ctx.beginPath();
      series.forEach((v, i) => (i ? ctx.lineTo(px(i), py(v)) : ctx.moveTo(px(i), py(v))));
      ctx.stroke();
    }
    function run() {
      let n = 0;
      const anim = () => {
        if (n++ >= 15) {
          readout.innerHTML = `ENP: <b>${series[series.length - 1].toFixed(2)}</b> (converged toward two)`;
          return;
        }
        cycle();
        render();
        readout.innerHTML = `Cycle ${n} · ENP: <b>${series[series.length - 1].toFixed(2)}</b>`;
        setTimeout(() => raf(anim), 90);
      };
      anim();
    }
    runBtn.addEventListener('click', run);
    resetBtn.addEventListener('click', seed);
    seed();
    window.addEventListener('resize', render);
    cleanups.push(() => window.removeEventListener('resize', render));
  })();

  // ---- real-world examples (rendered lazily when their tab is shown) -----
  const realRenderers = {};
  function realCanvas(key, height, capHTML, srcHTML) {
    const box = root.querySelector(`[data-real="${key}"]`);
    const cv = document.createElement('canvas');
    cv.style.height = `${height}px`;
    box.appendChild(cv);
    const cap = document.createElement('div');
    cap.className = 'cap';
    cap.innerHTML = capHTML;
    box.appendChild(cap);
    if (srcHTML) {
      const s = document.createElement('div');
      s.className = 'src';
      s.innerHTML = srcHTML;
      box.appendChild(s);
    }
    const ctx = cv.getContext('2d');
    const fit = () => {
      const dpr = window.devicePixelRatio || 1;
      const r = cv.getBoundingClientRect();
      cv.width = r.width * dpr;
      cv.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w: r.width, h: r.height };
    };
    return { ctx, fit };
  }

  // 1 · US ideological distribution: single-peak (1990s) → bimodal (2020s)
  (function () {
    const { ctx, fit } = realCanvas(
      'sampling',
      210,
      'Self-reported ideology of the US public. In the 1990s it was <b>single-peaked near the center</b>; by the 2010s–2020s it split into <b>two humps</b> as the parties pulled apart — precisely the “two clusters” landscape that speeds two-party consolidation.',
      'Source: Pew Research Center, <i>Political Polarization in the American Public</i> (2014); <i>The Partisan Divide on Political Values Grows Even Wider</i> (2017).'
    );
    const g = (x, mu, sd) => Math.exp(-((x - mu) ** 2) / (2 * sd * sd));
    realRenderers.sampling = function render() {
      const { w, h } = fit();
      ctx.clearRect(0, 0, w, h);
      const pad = 26;
      const X = (x) => pad + x * (w - 2 * pad);
      const base = h - 26;
      ctx.strokeStyle = '#26304a';
      ctx.beginPath();
      ctx.moveTo(pad, base);
      ctx.lineTo(w - pad, base);
      ctx.stroke();
      ctx.fillStyle = '#6b7897';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Liberal', X(0.06), base + 15);
      ctx.fillText('Moderate', X(0.5), base + 15);
      ctx.fillText('Conservative', X(0.94), base + 15);
      const curve = (fn, color) => {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let i = 0; i <= 120; i++) {
          const x = i / 120;
          const py = base - fn(x) * (base - 18);
          if (i) ctx.lineTo(X(x), py);
          else ctx.moveTo(X(x), py);
        }
        ctx.stroke();
        ctx.lineTo(X(1), base);
        ctx.lineTo(X(0), base);
        ctx.closePath();
        ctx.globalAlpha = 0.1;
        ctx.fillStyle = color;
        ctx.fill();
        ctx.globalAlpha = 1;
      };
      curve((x) => g(x, 0.5, 0.17), '#7ef0c2');
      curve((x) => Math.min(1, g(x, 0.28, 0.1) + g(x, 0.72, 0.1)), '#ff8f6e');
      ctx.textAlign = 'left';
      ctx.fillStyle = '#7ef0c2';
      ctx.fillText('■ ~1990s (centrist)', pad, 16);
      ctx.fillStyle = '#ff8f6e';
      ctx.fillText('■ ~2020s (polarized)', pad + 130, 16);
    };
  })();

  // 2 · Real party positions in a 2-D space (multiparty compass)
  (function () {
    const { ctx, fit } = realCanvas(
      'voronoi',
      240,
      'Approximate positions of German parties on an economic (L→R) and social (libertarian→authoritarian) map. Voters gravitate to the <b>nearest</b> party, carving the space into party “catchment” regions — proximity voting in practice.',
      'Positions approximated from the <i>Chapel Hill Expert Survey</i> (CHES) 2019, chesdata.eu.'
    );
    const parties = [
      { n: 'Die Linke', x: 0.14, y: 0.32, c: '#f472b6' },
      { n: 'Grüne', x: 0.34, y: 0.2, c: '#7ef0c2' },
      { n: 'SPD', x: 0.4, y: 0.44, c: '#ff8f6e' },
      { n: 'FDP', x: 0.66, y: 0.34, c: '#ffd257' },
      { n: 'CDU/CSU', x: 0.66, y: 0.6, c: '#6ea8fe' },
      { n: 'AfD', x: 0.86, y: 0.82, c: '#c8a8ff' },
    ];
    realRenderers.voronoi = function render() {
      const { w, h } = fit();
      const X = (x) => 8 + x * (w - 16);
      const Y = (y) => 8 + y * (h - 26);
      const step = 8;
      for (let sx = 0; sx < w; sx += step) {
        for (let sy = 0; sy < h - 18; sy += step) {
          const nx = (sx - 8) / (w - 16);
          const ny = (sy - 8) / (h - 26);
          let best = 0;
          let bd = Infinity;
          parties.forEach((p, i) => {
            const d = (p.x - nx) ** 2 + (p.y - ny) ** 2;
            if (d < bd) {
              bd = d;
              best = i;
            }
          });
          ctx.fillStyle = parties[best].c;
          ctx.globalAlpha = 0.14;
          ctx.fillRect(sx, sy, step, step);
        }
      }
      ctx.globalAlpha = 1;
      parties.forEach((p) => {
        ctx.fillStyle = p.c;
        ctx.beginPath();
        ctx.arc(X(p.x), Y(p.y), 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#0a0e18';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.fillStyle = '#e7ecf5';
        ctx.font = '700 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(p.n, X(p.x), Y(p.y) - 10);
      });
      ctx.fillStyle = '#6b7897';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(
        '◄ economic left · economic right ►   (dot height = social conservatism)',
        8,
        h - 4
      );
    };
  })();

  // 3 · The 2000 US spoiler: Florida
  (function () {
    const { ctx, fit } = realCanvas(
      'strategic',
      210,
      'Florida, 2000: Bush beat Gore by <b>537 votes</b> while Nader drew <b>97,488</b>. Exit polls suggested Nader voters preferred Gore ~2:1 — enough to flip the state and the presidency. Episodes like this teach voters to abandon non-viable third parties: Duverger’s <b>psychological effect</b>.',
      'Source: Florida Department of State, certified 2000 General Election results; Dave Leip’s Atlas of U.S. Presidential Elections (uselectionatlas.org).'
    );
    const rows = [
      { n: 'Bush', v: 2912790, c: '#ff8f6e' },
      { n: 'Gore', v: 2912253, c: '#6ea8fe' },
      { n: 'Nader', v: 97488, c: '#7ef0c2' },
    ];
    realRenderers.strategic = function render() {
      const { w, h } = fit();
      ctx.clearRect(0, 0, w, h);
      const max = 3000000;
      const bw = (w - 40) / rows.length;
      rows.forEach((r, i) => {
        const bh = (r.v / max) * (h - 60);
        const x = 20 + i * bw;
        ctx.fillStyle = r.c;
        ctx.fillRect(x + 10, h - 30 - bh, bw - 20, bh);
        ctx.fillStyle = '#cfe0ff';
        ctx.font = '700 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(r.v.toLocaleString(), x + bw / 2, h - 34 - bh);
        ctx.fillStyle = '#93a0bd';
        ctx.fillText(r.n, x + bw / 2, h - 10);
      });
      ctx.fillStyle = '#ffd257';
      ctx.font = '700 11px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('Bush − Gore margin: 537 votes  ·  Nader: 97,488', 20, 16);
    };
  })();

  // 4 · Alaska 2022 US House special — a real IRV tabulation
  (function () {
    const { ctx, fit } = realCanvas(
      'tally',
      230,
      'Alaska’s 2022 U.S. House special (ranked-choice). No one had a majority in round 1; Begich was eliminated and his ballots transferred — <b>Peltola (D) won 51.5%–48.5%</b> over Palin in a Republican-leaning state, an outcome plurality FPTP would not have produced.<br><b>Who uses what:</b> FPTP — US, UK, India · IRV — Australia, Maine, Alaska · PR/D’Hondt — Spain, Netherlands, Israel · Approval — Fargo & St. Louis.',
      'Source: Alaska Division of Elections, 2022 U.S. Representative Special Election, RCV tabulation (elections.alaska.gov).'
    );
    const r1 = [
      { n: 'Peltola', v: 40.19, c: '#6ea8fe' },
      { n: 'Palin', v: 31.27, c: '#ff8f6e' },
      { n: 'Begich', v: 28.53, c: '#c8a8ff' },
    ];
    const r2 = [
      { n: 'Peltola', v: 51.48, c: '#6ea8fe' },
      { n: 'Palin', v: 48.52, c: '#ff8f6e' },
    ];
    realRenderers.tally = function render() {
      const { w, h } = fit();
      ctx.clearRect(0, 0, w, h);
      const half = w / 2;
      const group = (rows, x0, ww, title) => {
        ctx.fillStyle = '#93a0bd';
        ctx.font = '700 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(title, x0 + ww / 2, 16);
        const bw = (ww - 20) / rows.length;
        rows.forEach((r, i) => {
          const bh = (r.v / 60) * (h - 70);
          const x = x0 + 10 + i * bw;
          ctx.fillStyle = r.c;
          ctx.fillRect(x + 6, h - 30 - bh, bw - 12, bh);
          ctx.fillStyle = '#cfe0ff';
          ctx.font = '700 11px sans-serif';
          ctx.fillText(`${r.v}%`, x + bw / 2, h - 34 - bh);
          ctx.fillStyle = '#93a0bd';
          ctx.fillText(r.n, x + bw / 2, h - 12);
        });
      };
      group(r1, 0, half, 'Round 1 (no majority)');
      group(r2, half, half, 'Final (Begich transfers) → Peltola wins');
      ctx.strokeStyle = '#26304a';
      ctx.beginPath();
      ctx.moveTo(half, 22);
      ctx.lineTo(half, h - 30);
      ctx.stroke();
    };
  })();

  // 5 · Real effective-number-of-parties by country
  (function () {
    const { ctx, fit } = realCanvas(
      'enp',
      220,
      'Effective number of parties for recent lower-house elections. <b>FPTP countries cluster near 2</b>; proportional systems sustain 5+. This cross-national pattern is the empirical core of Duverger’s Law.',
      'Source: Michael Gallagher, <i>Electoral Systems</i> dataset, Trinity College Dublin; index of Laakso & Taagepera (1979). Values approximate, recent elections.'
    );
    const rows = [
      { n: 'USA (FPTP)', v: 1.9, pr: false },
      { n: 'UK (FPTP)', v: 2.5, pr: false },
      { n: 'Canada (FPTP)', v: 2.8, pr: false },
      { n: 'Germany (MMP)', v: 4.8, pr: true },
      { n: 'Netherlands (PR)', v: 5.8, pr: true },
      { n: 'Israel (PR)', v: 7.0, pr: true },
    ];
    realRenderers.enp = function render() {
      const { w, h } = fit();
      ctx.clearRect(0, 0, w, h);
      const labelW = 120;
      const max = 8;
      const bx = labelW;
      const bw = w - labelW - 40;
      const rh = (h - 20) / rows.length;
      const two = bx + (2 / max) * bw;
      ctx.strokeStyle = '#2b5c46';
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(two, 6);
      ctx.lineTo(two, h - 6);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#7ef0c2';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('ENP = 2', two, h - 2);
      rows.forEach((r, i) => {
        const y = 8 + i * rh;
        ctx.fillStyle = '#aab6d4';
        ctx.font = '11px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(r.n, labelW - 8, y + rh / 2 + 3);
        ctx.fillStyle = r.pr ? '#7ef0c2' : '#6ea8fe';
        ctx.fillRect(bx, y + 4, (r.v / max) * bw, rh - 12);
        ctx.fillStyle = '#e7ecf5';
        ctx.font = '700 11px sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(r.v.toFixed(1), bx + (r.v / max) * bw + 6, y + rh / 2 + 3);
      });
    };
  })();

  // 6 · Median-voter repositioning (New Labour)
  (function () {
    const { ctx, fit } = realCanvas(
      'kmeans',
      210,
      'The centroid-chasing in the sim is real strategy: facing four straight defeats, UK Labour moved from the <b>left (1983)</b> toward the <b>centre (“New Labour”, 1997)</b> and won a landslide — Downs’ median-voter theorem in action.',
      'Sources: Anthony Downs, <i>An Economic Theory of Democracy</i> (1957); party-position estimates, Manifesto Project (MARPOR), manifesto-project.wzb.eu.'
    );
    realRenderers.kmeans = function render() {
      const { w, h } = fit();
      ctx.clearRect(0, 0, w, h);
      const pad = 26;
      const X = (x) => pad + x * (w - 2 * pad);
      const base = h - 30;
      const g = (x) => Math.exp(-((x - 0.5) ** 2) / (2 * 0.16 * 0.16));
      ctx.strokeStyle = '#26304a';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 0; i <= 120; i++) {
        const x = i / 120;
        const py = base - g(x) * (base - 40);
        if (i) ctx.lineTo(X(x), py);
        else ctx.moveTo(X(x), py);
      }
      ctx.stroke();
      ctx.fillStyle = '#6b7897';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('median voter', X(0.5), 16);
      ctx.fillText('Left', X(0.05), base + 16);
      ctx.fillText('Right', X(0.95), base + 16);
      const p1 = 0.28;
      const p2 = 0.5;
      ctx.strokeStyle = '#ffd257';
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      ctx.moveTo(X(p1), base - 20);
      ctx.lineTo(X(p2), base - 20);
      ctx.stroke();
      ctx.setLineDash([]);
      // arrowhead
      ctx.beginPath();
      ctx.moveTo(X(p2), base - 20);
      ctx.lineTo(X(p2) - 7, base - 24);
      ctx.lineTo(X(p2) - 7, base - 16);
      ctx.closePath();
      ctx.fillStyle = '#ffd257';
      ctx.fill();
      const dot = (x, label, color) => {
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(X(x), base - 20, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#0a0e18';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.fillStyle = '#e7ecf5';
        ctx.font = '700 10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(label, X(x), base - 32);
      };
      dot(p1, 'Labour 1983', '#ff8f6e');
      dot(p2, 'New Labour 1997', '#6ea8fe');
    };
  })();

  // 7 · Duverger empirically: ENP trajectories, FPTP vs PR
  (function () {
    const { ctx, fit } = realCanvas(
      'feedback',
      210,
      'Effective number of parties over ~70 years. The <b>US (FPTP)</b> hugs 2 for generations while the <b>Netherlands (PR)</b> stays near 5 — the durable, system-driven signature of Duverger’s Law.',
      'Sources: Michael Gallagher, <i>Electoral Systems</i> dataset (TCD); Arend Lijphart, <i>Patterns of Democracy</i> (1999). Values approximate.'
    );
    const years = [1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020];
    const us = [1.9, 1.9, 2.0, 2.0, 2.1, 2.0, 2.0, 1.9];
    const nl = [4.7, 5.0, 6.4, 4.8, 5.0, 5.4, 6.3, 5.9];
    realRenderers.feedback = function render() {
      const { w, h } = fit();
      ctx.clearRect(0, 0, w, h);
      const pad = 30;
      const maxY = 7;
      const px = (i) => pad + (i / (years.length - 1)) * (w - pad - 10);
      const py = (v) => h - 24 - (v / maxY) * (h - 40);
      for (let gg = 1; gg <= 6; gg++) {
        ctx.strokeStyle = gg === 2 ? '#2b5c46' : '#1c2438';
        ctx.setLineDash(gg === 2 ? [4, 4] : []);
        ctx.beginPath();
        ctx.moveTo(pad, py(gg));
        ctx.lineTo(w - 10, py(gg));
        ctx.stroke();
        ctx.fillStyle = gg === 2 ? '#7ef0c2' : '#6b7897';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(gg, pad - 4, py(gg) + 3);
      }
      ctx.setLineDash([]);
      const line = (arr, color) => {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        arr.forEach((v, i) => (i ? ctx.lineTo(px(i), py(v)) : ctx.moveTo(px(i), py(v))));
        ctx.stroke();
      };
      line(nl, '#7ef0c2');
      line(us, '#6ea8fe');
      ctx.textAlign = 'left';
      ctx.fillStyle = '#6ea8fe';
      ctx.font = '700 11px sans-serif';
      ctx.fillText('■ USA (FPTP)', pad, 14);
      ctx.fillStyle = '#7ef0c2';
      ctx.fillText('■ Netherlands (PR)', pad + 100, 14);
      ctx.fillStyle = '#6b7897';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('1950', px(0), h - 8);
      ctx.fillText('2020', px(years.length - 1), h - 8);
    };
  })();

  // tab switching: show demo or real, render real lazily (correct size on show)
  root.querySelectorAll('.mx-tab').forEach((t) => {
    t.addEventListener('click', () => {
      const key = t.dataset.for;
      const kind = t.dataset.kind;
      root
        .querySelectorAll(`.mx-tab[data-for="${key}"]`)
        .forEach((x) => x.classList.toggle('on', x === t));
      root.querySelector(`[data-demo="${key}"]`).style.display = kind === 'demo' ? '' : 'none';
      root.querySelector(`[data-real="${key}"]`).style.display = kind === 'real' ? '' : 'none';
      if (kind === 'real' && realRenderers[key]) realRenderers[key]();
    });
  });
  const onResizeReal = () => {
    Object.keys(realRenderers).forEach((k) => {
      const el = root.querySelector(`[data-real="${k}"]`);
      if (el && el.style.display !== 'none') realRenderers[k]();
    });
  };
  window.addEventListener('resize', onResizeReal);
  cleanups.push(() => window.removeEventListener('resize', onResizeReal));

  return () => cleanups.forEach((fn) => fn());
}
